import { useEffect, useState } from 'react'
import { ConnectTemplateDialog } from '@/components/procedures/ConnectTemplateDialog'
import {
  useConnectMaterialProcedure,
  useDisconnectMaterialProcedure,
} from '@/hooks/useInventoryMaster'
import { STATUS_LABELS } from './MaterialsTable'
import type { ApiMaterial } from '@/types/inventory'

interface ConnectProcedureDialogProps {
  /** The material being connected. Pass null to keep the dialog mounted-closed. */
  material: ApiMaterial | null
  open: boolean
  onClose: () => void
}

/**
 * Connect / replace / disconnect the Inventory procedure TEMPLATE that gates a
 * material's lots — the shared Connect Procedure Template dialog, offering
 * every active, Inventory-typed template. Each lot received against the
 * connected material spawns its OWN run of the template, and that run's forms
 * move the lot's gate status (quarantine → in process → available) — never
 * the material's directly. Connecting saves at once.
 */
export function ConnectProcedureDialog({ material, open, onClose }: ConnectProcedureDialogProps) {
  const [resultStatus, setResultStatus] = useState<string | null>(null)
  /**
   * The live connection, seeded from the material and then owned here.
   *
   * NOT read straight off the `material` prop: that object is captured by the
   * table when the dialog opens, so it still says `procedureTemplateId: null`
   * after a connect even though the row behind the dialog has already
   * refetched. The mutation returns the updated material, so take it from
   * there.
   */
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null)

  const connect = useConnectMaterialProcedure()
  const disconnect = useDisconnectMaterialProcedure()

  useEffect(() => {
    if (!open) return
    setResultStatus(null)
    setCurrentTemplateId(material?.procedureTemplateId ?? null)
  }, [open])

  return (
    <ConnectTemplateDialog
      open={open}
      onClose={onClose}
      templateType='Inventory'
      currentTemplateId={currentTemplateId}
      description={
        material
          ? `Connect ${material.materialId} · ${material.materialName} to the Inventory procedure template that gates its lots. Each lot received starts its own run of the template.`
          : 'Connect this material to an Inventory procedure template. Each lot received starts its own run of the template.'
      }
      onConnect={async (template) => {
        if (!material) return
        const updated = await connect.mutateAsync({ materialId: material.id, procedureTemplateId: template.id })
        setResultStatus(updated.status)
        setCurrentTemplateId(updated.procedureTemplateId ?? template.id)
      }}
      onDisconnect={async () => {
        if (!material) return
        const updated = await disconnect.mutateAsync(material.id)
        setResultStatus(updated.status)
        setCurrentTemplateId(updated.procedureTemplateId ?? null)
      }}
      note={
        resultStatus && (
          <p className='rounded-md bg-green-50 px-3 py-2 text-sm text-green-700'>
            Material status is now{' '}
            <span className='font-semibold'>
              {STATUS_LABELS[resultStatus as keyof typeof STATUS_LABELS] ?? resultStatus}
            </span>
            .
          </p>
        )
      }
    />
  )
}
